import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Percent } from 'lucide-react';
import { eur, eur2, num, monthsToLabel, pct, DISCLAIMER } from '../../lib/api';
import { useScenarioCompute } from '../../hooks/useAcquisition';
import { PaybackChart } from './PaybackChart';
import { TableScroll } from './TableScroll';

export function ScenariosSection({
  revealPrivate,
  scenarios,
  scenariosWithWC,
  scenariosNoDiscount,
  scenariosWithDiscount,
  price,
  wcAddition,
  onRequestAccess,
  preselectHint,
  applyDiscount,
  onApplyDiscountChange,
}) {
  const keys = Object.keys(scenarios || {});
  const [active, setActive] = useState(keys[0] || 'conservative');
  const [managementCost, setManagementCost] = useState(0);
  const [includeWC, setIncludeWC] = useState(false);
  const { compute } = useScenarioCompute();
  const [customResult, setCustomResult] = useState(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    if (preselectHint?.scenarioKey && keys.includes(preselectHint.scenarioKey)) {
      setActive(preselectHint.scenarioKey);
    }
  }, [preselectHint?.scenarioKey, keys]);

  // Reset active tab when scenarios change (e.g. new scenario added/removed).
  useEffect(() => {
    if (!keys.includes(active) && keys.length > 0) setActive(keys[0]);
  }, [keys.join(','), active]);

  const baseline = useMemo(() => (includeWC ? scenariosWithWC?.[active] : scenarios?.[active]), [active, includeWC, scenarios, scenariosWithWC]);

  useEffect(() => {
    if (managementCost === 0) {
      setCustomResult(null);
      return;
    }
    let cancelled = false;
    setComputing(true);
    compute({ scenarioKey: active, managementCost, includeWorkingCapital: includeWC, applyDiscount })
      .then((r) => { if (!cancelled) setCustomResult(r); })
      .catch(() => { if (!cancelled) setCustomResult(null); })
      .finally(() => { if (!cancelled) setComputing(false); });
    return () => { cancelled = true; };
  }, [active, managementCost, includeWC, applyDiscount, compute]);

  const view = customResult || baseline;
  if (!view) return null;

  const activeScenario = scenarios?.[active];
  const activeColor = activeScenario?.color || '#334155';
  // Payback and ROI both tie to the capital actually considered in the
  // simulation (acquisition + transaction + working capital when included).
  const capitalBase = view.capital_total_eur;
  const paybackMonths = view.payback_total_capital_months;
  const paybackAgainstAcq = view.payback_acquisition_months;

  // Comparison: how much does the discount cost in year-1 revenue?
  const yr1WithDiscount = scenariosWithDiscount?.[active]?.years?.[0];
  const yr1NoDiscount = scenariosNoDiscount?.[active]?.years?.[0];
  const revenueDelta = yr1WithDiscount && yr1NoDiscount ? yr1WithDiscount.revenue_net - yr1NoDiscount.revenue_net : null;
  const ebitdaDelta = yr1WithDiscount && yr1NoDiscount ? yr1WithDiscount.ebitda - yr1NoDiscount.ebitda : null;

  return (
    <section id="cenarios" className="acq-section">
      <div className="acq-container">
        <header>
          <span className="kicker">Cenários financeiros</span>
          <h2>Cenários moderados, explicáveis e comparáveis lado a lado.</h2>
          <p className="section-lead">
            Todos os números derivam da mesma configuração financeira. Alterne cenário, ative o desconto por
            modelo ou simule com gestão delegada para ver o efeito imediato no EBITDA, na caixa e no payback.
          </p>
        </header>
        <div className="body">
          {preselectHint?.label && (
            <div className="preselect-notice" data-testid="preselect-notice">
              <Sparkles size={14} />
              <div>
                <strong>Pré-seleção a partir do plano em edição:</strong> {preselectHint.label}
              </div>
            </div>
          )}

          <div className="scenarios-toolbar">
            <div className="scenario-tabs" role="tablist" data-testid="scenario-tabs">
              {keys.map((key) => {
                const sc = scenarios[key];
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    role="tab"
                    className="scenario-tab"
                    aria-pressed={isActive}
                    onClick={() => setActive(key)}
                    data-testid={`scenario-${key}`}
                    style={isActive ? { boxShadow: `inset 0 -3px 0 0 ${sc.color || '#334155'}` } : {}}
                  >
                    <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: sc.color || '#334155', marginRight: 6, verticalAlign: 'middle' }} />
                    {sc.label}
                  </button>
                );
              })}
            </div>
            <div className="scenarios-toolbar-right">
              <label className="toggle-row toggle-discount" style={{ cursor: 'pointer' }} data-testid="toggle-discount-row">
                <input
                  type="checkbox"
                  checked={!!applyDiscount}
                  onChange={(e) => onApplyDiscountChange?.(e.target.checked)}
                  data-testid="toggle-apply-discount"
                />
                <div>
                  <label><Percent size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Simular desconto adicional</label>
                  <div className="hint">Os preços já incluem o desconto documental. Isto aplica uma redução extra por modelo (simulação).</div>
                </div>
              </label>
              {revealPrivate && (
                <label className="toggle-row" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeWC}
                    onChange={(e) => setIncludeWC(e.target.checked)}
                    data-testid="toggle-wc"
                  />
                  <div>
                    <label>Incluir fundo de maneio ({eur(wcAddition, 0)})</label>
                    <div className="hint">A confirmar. Sem impacto no cenário-base do plano.</div>
                  </div>
                </label>
              )}
              <label className="toggle-row" style={{ cursor: 'pointer' }}>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={managementCost}
                  onChange={(e) => setManagementCost(Math.max(0, Number(e.target.value) || 0))}
                  style={{ width: 100, padding: '.35rem .5rem', border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }}
                  data-testid="management-cost"
                />
                <div>
                  <label>Custo anual de gestão (€)</label>
                  <div className="hint">Aplica-se se comprador não operar o negócio.</div>
                </div>
              </label>
            </div>
          </div>

          {applyDiscount && revenueDelta !== null && (
            <div className="discount-impact-banner" data-testid="discount-impact-banner" style={{ borderLeft: `3px solid ${activeColor}` }}>
              <Percent size={16} />
              <div>
                <strong>Desconto adicional simulado:</strong> além do desconto documental já incluído nos preços, esta redução extra baixa a receita do ano 1 em <b>{eur(-revenueDelta, 0)}</b> (EBITDA {eur(ebitdaDelta, 0)}) neste cenário.
                {(() => {
                  const neg = (view.years?.[0]?.per_model || []).filter((m) => m.contribution_negative);
                  if (!neg.length) return null;
                  return (
                    <span className="hint" style={{ display: 'block', marginTop: '.35rem', color: '#b91c1c' }}>
                      Contribuição negativa após custo com buffer e provisão: {neg.map((m) => `${m.name} (${eur2(m.unit_contribution)}/un.)`).join(', ')}. Uma redução extra torna estes modelos deficitários.
                    </span>
                  );
                })()}
                {' '}
                <span className="hint">É uma simulação; não é o preço praticado.</span>
              </div>
            </div>
          )}

          <div className="kpi-grid" data-testid="scenario-kpis">
            {revealPrivate && (
              <div className="kpi" style={{ borderTop: `3px solid ${activeColor}` }}>
                <div className="label">Payback (capital considerado)</div>
                <div className="value">{monthsToLabel(paybackMonths)}</div>
                <div className="hint">Base: {eur(capitalBase, 0)} (aquisição + transação{includeWC ? ' + fundo de maneio' : ''}).{paybackAgainstAcq ? ` Só aquisição (${eur(view.acquisition_price_eur, 0)}): ${monthsToLabel(paybackAgainstAcq)}.` : ''}</div>
              </div>
            )}
            <div className="kpi" style={{ borderTop: `3px solid ${activeColor}` }}>
              <div className="label">EBITDA ano 1</div>
              <div className="value">{eur(view.years[0].ebitda, 0)}</div>
              <div className="hint">Antes de depreciação e imposto.{applyDiscount ? ' Com desconto aplicado.' : ''}</div>
            </div>
            <div className="kpi" style={{ borderTop: `3px solid ${activeColor}` }}>
              <div className="label">Caixa operacional 3 anos</div>
              <div className="value">{eur(view.total_operating_cash_3y, 0)}</div>
              <div className="hint">Soma da caixa operacional dos 3 anos.</div>
            </div>
            <div className="kpi" style={{ borderTop: `3px solid ${activeColor}` }} data-testid="scenario-breakeven">
              <div className="label">Break-even ano 1</div>
              <div className="value">{view.break_even?.units_equivalent != null ? `${num(view.break_even.units_equivalent, 1)} un.` : 'Sem equilíbrio'}</div>
              <div className="hint">
                {view.break_even?.units_equivalent != null
                  ? `≈ ${eur(view.break_even.revenue_net, 0)} de receita · aprox. ${view.break_even.units_rounded} equipamentos equivalentes (depende do mix do cenário)`
                  : 'A contribuição não cobre os custos fixos neste cenário.'}
              </div>
            </div>
            {revealPrivate && (
              <div className="kpi" style={{ borderTop: `3px solid ${activeColor}` }} data-testid="scenario-roi">
                <div className="label">Retorno acumulado</div>
                <div className="value">{pct(view.accumulated_return_percent_on_capital, 1)}</div>
                <div className="hint">Sobre o capital considerado ({eur(capitalBase, 0)}). Fluxos operacionais 3 anos; sem valor de revenda.</div>
              </div>
            )}
          </div>

          {!revealPrivate && (
            <div className="alert alert-info" style={{ marginTop: '1.25rem' }} data-testid="scenario-locked">
              <div>
                <strong>Payback, retorno acumulado e sensibilidade completa</strong> ficam disponíveis dentro do data room, mediante palavra-passe.{' '}
                <button className="btn btn-sm btn-accent" style={{ marginLeft: '.5rem' }} onClick={onRequestAccess} data-testid="scenario-request-access">
                  Solicitar acesso
                </button>
              </div>
            </div>
          )}

          <TableScroll style={{ marginTop: '2rem' }}>
          <table className="data-table" data-testid="scenario-table">
            <thead>
              <tr>
                <th>Ano</th>
                <th className="num">Unidades</th>
                <th className="num">Receita s/IVA</th>
                <th className="num">Custo direto ajustado</th>
                <th className="num">Provisão variável</th>
                <th className="num">Custos fixos</th>
                <th className="num">EBITDA</th>
                <th className="num">Resultado líquido</th>
                <th className="num">Caixa operacional</th>
              </tr>
            </thead>
            <tbody>
              {view.years.map((y) => (
                <tr key={y.year}>
                  <td>{y.year}</td>
                  <td className="num">{y.units}</td>
                  <td className="num">{eur(y.revenue_net, 0)}</td>
                  <td className="num">{eur(y.direct_cost_adjusted, 0)}</td>
                  <td className="num">{eur(y.variable_provision, 0)}</td>
                  <td className="num">{eur(y.fixed_costs, 0)}</td>
                  <td className="num">{eur(y.ebitda, 0)}</td>
                  <td className="num">{eur(y.net_profit, 0)}</td>
                  <td className="num">{eur(y.operating_cash, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableScroll>
          {computing && <p className="hint" style={{ marginTop: '.75rem', fontSize: '.75rem', color: 'var(--muted)' }}>A recalcular...</p>}

          <PaybackChart scenario={view} price={price} capitalBase={revealPrivate ? capitalBase : null} paybackMonths={paybackMonths} paybackFirstFull={view.payback_total_first_full_month} includeWC={includeWC} revealPrivate={revealPrivate} />

          <div style={{ marginTop: '2rem', display: 'grid', gap: '.75rem' }}>
            <div className="alert alert-info" data-testid="scenario-caveats">
              <div>
                <strong>Leituras obrigatórias:</strong> projeção para proprietário-operador; não inclui remuneração do comprador.
                A rentabilidade de uma gestão passiva será inferior e depende do custo da equipa contratada.
                O fundo de maneio adicional não está confirmado. O retorno é calculado sobre fluxos operacionais;
                não é rendimento garantido nem inclui valor de revenda.
              </div>
            </div>
            <p style={{ fontSize: '.78rem', color: 'var(--muted)', margin: 0 }}>{DISCLAIMER}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
